import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { formatDateTime } from "../../../globalFunctions/DateTimeDisplayCleaner";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../../../globalFunctions/PhoneNumberFormatter";
import {
  PlusIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import FollowUpModal from "./tabs/popups/FollowUpModal";
import FollowUpDetailsModal from "./tabs/popups/FollowUpDetailsModal";
import CancelFollowUpModal from "./tabs/popups/CancelFollowUpModal";
import CompleteFollowUpModal from "./tabs/popups/CompleteFollowUpModal";

export default function RepresentativeFollowUpsTable() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const currentUserId = currentUser?.id;
  const navigate = useNavigate();

  const [followUps, setFollowUps] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [cancelingFollowUpId, setCancelingFollowUpId] = useState(null);
  const [completingFollowUpId, setCompletingFollowUpId] = useState(null);
  const [cancelReasonInput, setCancelReasonInput] = useState("");
  const [completedNoteInput, setCompletedNoteInput] = useState("");

  const followUpTypes = {
    1: "followUps.followUpType.inPerson",
    2: "followUps.followUpType.phone",
    3: "followUps.followUpType.email",
  };

  const followUpStatuses = {
    1: "followUps.status.inProgress",
    2: "followUps.status.completed",
    3: "followUps.status.canceled",
  };

  const typeIcons = {
    1: <UserIcon className="h-4 w-4 inline-block mr-1" />,
    2: <PhoneIcon className="h-4 w-4 inline-block mr-1" />,
    3: <EnvelopeIcon className="h-4 w-4 inline-block mr-1" />,
  };

  useEffect(() => {
    if (currentUserId) loadFollowUps();
  }, [currentUserId]);

  const loadFollowUps = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/followups/representative`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": currentUserId,
          },
        }
      );

      if (!res.ok) throw new Error("Erreur chargement des suivis");

      const data = await res.json();
      setFollowUps(data);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des suivis.");
    } finally {
      setLoading(false);
    }
  };

  const filteredFollowUps = followUps.filter(
    (f) =>
      f.userName?.toLowerCase().includes(search.toLowerCase()) ||
      f.note?.toLowerCase().includes(search.toLowerCase())
  );

  const getRowBgColor = (f) => {
    if (f.status !== 1) return "";

    const dueDate = new Date(f.dueDate);
    const now = new Date();

    const dueDateOnly = new Date(
      dueDate.getFullYear(),
      dueDate.getMonth(),
      dueDate.getDate()
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const timeDiff = dueDateOnly.getTime() - today.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (daysDiff <= 0) return "#fee2e2"; // Rouge (en retard ou aujourd'hui)
    if (daysDiff === 1 || daysDiff === 2) return "#fef9c3"; // Jaune (demain ou après-demain)

    return "";
  };

  const handleStatusChange = async (
    followUpId,
    status,
    cancelReason,
    completedNote
  ) => {
    try {
      const payload = {
        status,
        cancelReason,
        completedNote,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/followups/${followUpId}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Erreur lors de la mise à jour du suivi");

      const updatedFollowUp = await res.json();

      // ✅ Mise à jour dans la liste globale
      setFollowUps((prev) =>
        prev.map((f) => (f.id === updatedFollowUp.id ? updatedFollowUp : f))
      );

      // ✅ Mise à jour dans le modal (si sélectionné)
      setSelectedFollowUp((prev) =>
        prev?.id === updatedFollowUp.id ? updatedFollowUp : prev
      );

      // ✅ Réinitialisation des champs
      setCancelReasonInput("");
      setCompletedNoteInput("");
      setCancelingFollowUpId(null);
      setCompletingFollowUpId(null);
      loadFollowUps();
    } catch (err) {
      alert("Erreur: " + err.message);
    }
  };

  return (
    <div className="p-6 space-y-6 text-primary">
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        {/* Titre à gauche */}
        <h1 className="text-2xl font-bold w-full md:w-auto">Mes suivis</h1>

        {/* Champ de recherche + bouton à droite */}
        <div className="flex w-full md:w-auto gap-2">
          <input
            type="text"
            placeholder="Rechercher par utilisateur ou note..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input input-bordered w-full md:w-64"
          />
          <button
            onClick={() => setIsOpenModal(true)}
            className="btn bg-primary text-white rounded-full flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Ajouter un suivi
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center">Chargement...</div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : filteredFollowUps.length === 0 ? (
        <div className="text-center text-gray-500">Aucun suivi trouvé.</div>
      ) : (
        <div className="overflow-x-auto border rounded-lg bg-base-100 shadow-sm">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Type</th>
                <th>Note</th>
                <th>Contact</th>
                <th>Statut</th>
                <th>Date prévue</th>
                <th>Créé le</th>
              </tr>
            </thead>
            <tbody>
              {filteredFollowUps.map((f) => {
                const rowBackgroundColor = getRowBgColor(f);
                return (
                  <tr
                    key={f.id}
                    style={{ backgroundColor: rowBackgroundColor }}
                  >
                    <td>{f.userName || "-"}</td>

                    <td>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full border text-primary/80 ${
                          f.followUpType === 1
                            ? "bg-pink-200"
                            : f.followUpType === 2
                            ? "bg-blue-200"
                            : "bg-yellow-200"
                        }`}
                      >
                        {typeIcons[f.followUpType]}
                        {t(followUpTypes[f.followUpType])}
                      </span>
                    </td>

                    <td>{f.note}</td>

                    <td>
                      {f.email && (
                        <a
                          href={`mailto:${f.email}`}
                          className="text-blue-600 underline block"
                        >
                          {f.email}
                        </a>
                      )}
                      {f.phoneNumber && (
                        <a
                          href={`tel:${f.phoneNumber}`}
                          className="text-blue-600 underline block"
                        >
                          {formatPhoneNumber(f.phoneNumber)}
                        </a>
                      )}
                      {f.address && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            f.address
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline block"
                        >
                          {f.address}
                        </a>
                      )}
                    </td>

                    <td>
                      {f.status === 1 && (
                        <span className="badge badge-warning">En cours</span>
                      )}
                      {f.status === 2 && (
                        <span className="badge badge-success">Complété</span>
                      )}
                      {f.status === 3 && (
                        <span className="badge badge-error">Annulé</span>
                      )}
                    </td>

                    <td>{formatDateTime(f.dueDate)}</td>
                    <td>{formatDateTime(f.createdAt)}</td>
                    <td>
                      <button
                        onClick={() => {
                          setSelectedFollowUp(f);
                          setShowDetailsModal(true);
                        }}
                        className="btn btn-sm bg-primary text-white rounded-full"
                      >
                        Voir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {isOpenModal && (
        <FollowUpModal
          isOpen={isOpenModal}
          currentUserId={currentUserId}
          onClose={() => {
            setIsOpenModal(false);
            loadFollowUps();
          }}
          onSave={loadFollowUps}
        />
      )}

      {showDetailsModal && selectedFollowUp && (
        <FollowUpDetailsModal
          key={selectedFollowUp?.id}
          isOpen={showDetailsModal}
          followUp={selectedFollowUp}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedFollowUp(null);
            loadFollowUps();
          }}
          onRequestCancel={(id) => setCancelingFollowUpId(id)}
          onRequestComplete={(id) => setCompletingFollowUpId(id)}
        />
      )}

      <CancelFollowUpModal
        isOpen={cancelingFollowUpId !== null}
        reason={cancelReasonInput}
        onChangeReason={setCancelReasonInput}
        onClose={() => setCancelingFollowUpId(null)}
        onConfirm={(finalReason) =>
          handleStatusChange(cancelingFollowUpId, 3, finalReason, null)
        }
      />

      <CompleteFollowUpModal
        isOpen={completingFollowUpId !== null}
        note={completedNoteInput}
        onChangeNote={setCompletedNoteInput}
        onClose={() => setCompletingFollowUpId(null)}
        onConfirm={(finalNote) =>
          handleStatusChange(completingFollowUpId, 2, null, finalNote)
        }
      />
    </div>
  );
}
